import { useGetAllDocuments } from '@modusign/openapi-client-document';
import {
  useGetAuthority,
  useGetFeatureUsages,
  useGetSubscription,
} from '@modusign/openapi-client-subscription';
import {
  GetFeatureUsagesResponseLimitFeature,
  GetFeatureUsagesResponsePermissionResource,
  GetFeatureUsagesResponseUsageFeature,
} from '@modusign/openapi-client-subscription/model';
import {
  useGetRole,
  useGetUserInfo,
  useGetWorkspace,
  useGetWorkspaceSurvey,
  useListWorkspaces,
} from '@modusign/openapi-client-user';
import { RolePermissionResponseName } from '@modusign/openapi-client-user/model';
import { useRequester } from '@modusign/react-signing';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useUpdateEffect } from 'react-use';

import { useRoutePath } from '@app/components/Route/RoutePathProvider';
import useContractLinkList from '@app/features/contractLink/contractLinkManagement/hooks/useContractLinkList';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import authService from '@app/libs/auth/authService';
import documentAuthService from '@app/libs/auth/documentAuthService';
import findActiveWorkspace from '@app/libs/workspaceService/findActiveWorkspace';
import { participantSelector } from '@app/store/participant/selectors';
import { getHasContractLinkPermission } from '@app/store/user/authority/selectors';

function useDataLayer() {
  const workspaceId = useWorkspaceId();
  const userId = authService.getSub();

  const participant = useSelector(participantSelector);
  const hasUserContractLinkPermission = useSelector(getHasContractLinkPermission);

  const { requester } = useRequester();

  const { isPermitted: hasPaymentPermission, isSuccess: isHasPaymentPermissionSuccess } =
    useMemberPermissions('MANAGE_PAYMENT');

  const location = useLocation();
  const routePath = useRoutePath();

  useEffect(() => {
    if (userId) {
      pushDataLayer({ event: 'identify', userId });
    }
  }, [userId]);

  // 활성화된 유저 토큰이 없어진 경우 데이터 레이어에 로그아웃으로 반영
  useUpdateEffect(() => {
    if (!userId) {
      pushDataLayer({ event: 'reset', userId: undefined, workspaceId: undefined });
      pushDataLayer({ event: 'userUpdated', user: undefined });
      pushDataLayer({
        event: 'workspacesUpdated',
        publicWorkspaceId: undefined,
        privateWorkspaceId: undefined,
        activeWorkspaceId: undefined,
      });
      pushDataLayer({ event: 'workspaceUpdated', workspace: undefined });
      pushDataLayer({ event: 'roleUpdated', rolePermissions: undefined });
      pushDataLayer({ event: 'workspaceSurveyUpdated', workspaceSurvey: undefined });
      pushDataLayer({ event: 'countOfDocumentsUpdated', countOfDocuments: undefined });
      pushDataLayer({ event: 'hasPaymentPermissionUpdated', hasPaymentPermission: undefined });
      pushDataLayer({ event: 'subscriptionUpdated', subscription: undefined });
      pushDataLayer({ event: 'userAuthorityUpdated', userAuthority: undefined });
      pushDataLayer({
        event: 'featureUsagesUpdated',
        featureUsages: undefined,
        usages: undefined,
        permissions: undefined,
        limits: undefined,
      });
    }
  }, [userId]);

  useEffect(() => {
    pushDataLayer({ event: 'workspaceIdUpdated', workspaceId: workspaceId || undefined });
  }, [workspaceId]);

  // 결제 권한 변경 시 데이터 레이어에 반영
  useEffect(() => {
    if (workspaceId && isHasPaymentPermissionSuccess) {
      pushDataLayer({ event: 'hasPaymentPermissionUpdated', workspaceId, hasPaymentPermission });
    }
  }, [hasPaymentPermission, isHasPaymentPermissionSuccess, workspaceId]);

  // 링크 서명 개수 변경 시 데이터 레이어에 반영
  const { refetch } = useContractLinkList({
    enabled: location.pathname === '/links' && hasUserContractLinkPermission,
    page: 1,
    limit: 1,
    status: 'ACTIVE',
    onSuccess({ count }) {
      pushDataLayer({
        event: 'activeContractLinkCountUpdated',
        activeContractLinkCount: count,
      });
    },
  });

  useEffect(() => {
    if (location.pathname === '/links') {
      refetch();
    }
  }, [location, refetch]);

  // 유저 설문 정보 데이터 레이어에 반영
  useGetWorkspaceSurvey(workspaceId, {
    query: {
      onSuccess: (workspaceSurvey) => {
        pushDataLayer({
          event: 'workspaceSurveyUpdated',
          workspaceId,
          workspaceSurvey: workspaceSurvey || undefined,
        });
      },
      enabled: !!workspaceId && !documentAuthService.isLinkUser(),
    },
  });

  // 조회 가능 문서 개수 변경 시 데이터 레이어에 반영
  useGetAllDocuments(
    workspaceId,
    { limit: 1, offset: 0 },
    {
      query: {
        onSuccess: (data) => {
          pushDataLayer({
            event: 'countOfDocumentsUpdated',
            workspaceId,
            countOfDocuments: data.count,
          });
        },
        enabled: !!workspaceId && !documentAuthService.isLinkUser(),
      },
    }
  );

  // 동일 페이지에 1초 이상 머무는 경우에 페이지 뷰 이벤트를 전송
  useEffect(() => {
    const pushPageViewEvent = setTimeout(() => {
      pushDataLayer({ event: 'pageView', page: { routePath: routePath.current } });
    }, 1000);
    return () => clearTimeout(pushPageViewEvent);
  }, [location.pathname, location.search, routePath]);

  // 유저가 속한 웤스들 변경 시 데이터 레이어에 반영
  useListWorkspaces({
    query: {
      onSuccess: (workspaces) => {
        pushDataLayer({
          event: 'workspacesUpdated',
          publicWorkspaceId: workspaces.workspaces.find((workspace) => workspace.type === 'PUBLIC')
            ?.id,
          privateWorkspaceId: workspaces.workspaces.find(
            (workspace) => workspace.type === 'PRIVATE'
          )?.id,
          activeWorkspaceType: findActiveWorkspace(workspaces.workspaces).type,
        });
      },
      enabled: !documentAuthService.isLinkUser() && authService.hasAccessToken(),
    },
  });

  // 워크스페이스 변경 시 데이터 레이어에 반영
  const { data: workspace } = useGetWorkspace(workspaceId, {
    query: {
      onSuccess: (workspace) => {
        pushDataLayer({
          event: 'workspaceUpdated',
          workspaceId,
          workspace: workspace || undefined,
        });
      },
      enabled: !!workspaceId && !documentAuthService.isLinkUser(),
    },
  });

  // 유저의 워크스페이스 권한 변경 시 데이터 레이어에 반영
  useGetRole(workspaceId, workspace?.role?.id as string, {
    query: {
      onSuccess: (role) => {
        pushDataLayer({
          event: 'roleUpdated',
          workspaceId,
          rolePermissions: Object.keys(RolePermissionResponseName).reduce<{
            [key: string]: boolean;
          }>((acc, cur) => {
            acc[cur] = role.permissions.some((permission) => permission.name === cur);
            return acc;
          }, {}),
        });
      },
      enabled: !!workspace?.role?.id && !!workspaceId && !documentAuthService.isLinkUser(),
    },
  });

  // 유저 정보 변경 시 데이터 레이어에 반영
  useGetUserInfo({
    query: {
      onSuccess: (user) => {
        pushDataLayer({ event: 'userUpdated', user: user || undefined });
      },
      enabled: !documentAuthService.isLinkUser() && authService.hasAccessToken(),
    },
  });

  // 구독 정보 변경 시 데이터 레이어에 반영
  useGetSubscription(workspaceId, {
    query: {
      onSuccess: (subscription) => {
        pushDataLayer({
          event: 'subscriptionUpdated',
          workspaceId,
          subscription: subscription || undefined,
        });
      },
      enabled: !!workspaceId && !documentAuthService.isLinkUser(),
    },
  });

  // 사용 기능 기능 변경 시 데이터 레이어에 반영
  useGetFeatureUsages(workspaceId, {
    query: {
      onSuccess: (featureUsages) => {
        if (featureUsages) {
          pushDataLayer({
            event: 'featureUsagesUpdated',
            workspaceId,
            featureUsages,
            usages: Object.keys(GetFeatureUsagesResponseUsageFeature).reduce<{
              [key: string]: number;
            }>((acc, cur) => {
              acc[cur] = featureUsages.usages.find((usage) => usage.feature === cur)?.count || 0;
              return acc;
            }, {}),
            permissions: Object.keys(GetFeatureUsagesResponsePermissionResource).reduce<{
              [key: string]: boolean;
            }>((acc, cur) => {
              acc[cur] = featureUsages.permissions.some(
                (permission) => permission.id && permission.resource === cur
              );
              return acc;
            }, {}),
            limits: Object.keys(GetFeatureUsagesResponseLimitFeature).reduce<{
              [key: string]: number;
            }>((acc, cur) => {
              acc[cur] = featureUsages.limits.find((limit) => limit.feature === cur)?.count || 0;
              return acc;
            }, {}),
          });
          return;
        }
        pushDataLayer({
          event: 'featureUsagesUpdated',
          workspaceId,
          featureUsages: undefined,
          usages: undefined,
          permissions: undefined,
          limits: undefined,
        });
      },
      enabled: !!workspaceId && !documentAuthService.isLinkUser(),
    },
  });

  // 유저 권한 변경 시 데이터 레이어에 반영
  useGetAuthority(workspaceId, {
    query: {
      onSuccess: (userAuthority) => {
        pushDataLayer({
          event: 'userAuthorityUpdated',
          workspaceId,
          userAuthority: userAuthority || undefined,
        });
      },
      enabled: !!workspaceId && !documentAuthService.isLinkUser(),
    },
  });

  // 서명 참여자 정보 변경 시 데이터 레이어에 반영
  useUpdateEffect(() => {
    if (participant.documentId) {
      pushDataLayer({
        event: 'participantUpdated',
        participant: participant.requester.name
          ? participant
          : { ...participant, requester: { email: requester?.contact, name: requester?.name } },
      });
    }
  }, [participant, requester]);
}

export default useDataLayer;
